/**
 * Checks if the current user has the required permissions to view the content of the element.
 * If the user has the required permissions, the content of the element is displayed.
 * If the user does not have the required permissions, the content of the element is hidden.
 *
 * @param permissions - An array of strings representing the permissions required to view the content of the element.
 * @param requirement - A string indicating the requirement for the permissions. The value can be one of "all", "some", or "none".
 *                      If the value is "all", the user must have all of the specified permissions to view the content of the element.
 *                      If the value is "some", the user must have at least one of the specified permissions to view the content of the element.
 *                      If the value is "none", the user must not have any of the specified permissions to view the content of the element.
 *
 * @example
 * ```html
 * <div *permissionChecker="{ permissions: ['view-users', 'view-boxes'], requirement: 'all' }; else noPermission">
 *   This content will only be displayed if the current user has both "view-users" and "view-boxes" permissions.
 * </div>
 * <ng-template #noPermission>
 *   <div>You require some additional permissions to view this, please speak to your supervisor or manager  to issue you with the required roles and permissions. permission
        please speak to your system administrator.</div>
 * </ng-template>
 *
 * <div *permissionChecker="{ permissions: ['edit-users'], requirement: 'some' }>
 *   This content will only be displayed if the current user has the "edit-users" permission.
 * </div>
 *
 *
 * ```
 */

// TODO WOULD BE NICE IF WE COULD PASS A TEMPLATE TO THIS SO WE DONT HAVE TO DO TWO CHECKS AND JUST USE AN ELSE SOMEHOW

import {
  Directive,
  Input,
  OnInit,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { getSelectedOrganizationInfo } from '@boma/supplier-portal/shared/state';
import { take } from 'rxjs/operators';

interface PermissionCheckInput {
  permissions: string[];
  requirement?: 'some' | 'all' | 'none';
}

@Directive({
  selector: '[permissionChecker]',
})
export class PermissionCheckerDirective implements OnInit {
  @Input('permissionChecker') input: PermissionCheckInput;
  @Input('permissionCheckerElse') noPermission: TemplateRef<any>;

  constructor(
    private store: Store,
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef
  ) {}

  ngOnInit() {
    this.store
      .select(getSelectedOrganizationInfo)
      .pipe(take(1))
      .subscribe((org) => {
        this.checkPermission(org.permissions);
      });
  }

  private checkPermission(permissions: string[]) {
    if (!permissions) {
      this.viewContainer.clear();
      return;
    }
    const requirement = this.input?.requirement
      ? this.input?.requirement
      : 'some';

    const filteredPermissions = permissions.filter((p) =>
      this.input.permissions?.includes(p)
    );
    const hasPermission =
      this.input.requirement === 'none'
        ? !filteredPermissions.length
        : this.input.requirement === 'all'
        ? filteredPermissions.length === this.input.permissions.length
        : filteredPermissions.length > 0;

    if (hasPermission) {
      this.viewContainer.createEmbeddedView(this.templateRef);
    } else {
      if (this.noPermission)
        this.viewContainer.createEmbeddedView(this.noPermission);
    }
  }
}
